import React, { useState, useMemo, useDeferredValue, useEffect } from "react";
import { useRoute, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { ChevronRight, Search, Video, Clock, ChevronDown, ChevronUp, Play, User, CalendarIcon, Key, Shield, ChevronLeft } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../../utils/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NGROK } from "../../../APIs";
import { fetchAllRecording } from "./Api/weburlapi";
import { WebUrlRecording } from "./interface";
import { useNavigate } from "react-router-dom";
import { myLocalStorage } from "../../StorageHelper";

export default function WebUrlsDetails() {
    const [, params] = useRoute("webUrls/webUrldetails/:id");
    const urlId = params?.id;
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedSessions, setExpandedSessions] = useState<Record<string, boolean>>({});
    const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
    const [sessions, setSessions] = useState<WebUrlRecording[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [videoLoading, setVideoLoading] = useState(false);
    const [open, setOpen] = useState<boolean>(false);
    let selectedWebUrl = localStorage.getItem("selectedWebUrl") || "";
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const [selectedZip, setSelectedZip] = useState("");
    const [selectedFile, setSelectedFile] = useState("");

    const navigate = useNavigate();

    const itemsPerPage = 10;
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const getAllRecording = async (urlId: string) => {
        setLoading(true);
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";

            const response: WebUrlRecording[] = await fetchAllRecording(urlId, tenantName);
            if (response) {
                const sortedSessions = [...response].sort(
                    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
                );
                setSessions(sortedSessions);
            }
        } catch (err) {
            console.error("Failed to fetch recordings:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [deferredSearchTerm]);

    useEffect(() => {
        if (urlId) getAllRecording(urlId);
    }, [urlId]);


    const filteredSessions = useMemo(() => {
        if (!deferredSearchTerm.trim()) return sessions;

        const query = deferredSearchTerm.toLowerCase();
        return sessions.filter((session) =>
            Object.values(session).some((value) => {
                if (typeof value === "string") return value.toLowerCase().includes(query);
                if (Array.isArray(value)) return value.some(v => v.toLowerCase().includes(query));
                return false;
            })
        );
    }, [deferredSearchTerm, sessions]);

    const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSessions = filteredSessions.slice(startIndex, endIndex);

    const toggleSession = (sessionId: string) => {
        setExpandedSessions(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
    };

    const handleViewVideo = (zipName: string, videoFile: string) => {
        setSelectedZip(zipName);
        setSelectedFile(videoFile);
        setVideoLoading(true);
        const innerUrl = `${NGROK}/api/websites/stream-video?zipFile=${encodeURIComponent(zipName)}&videoFile=${encodeURIComponent(videoFile)}`;
        // Encode for iframe player
        const playerUrl = `${NGROK}/video-player?url=${encodeURIComponent(innerUrl)}`;

        setSelectedVideo(playerUrl);
        setIsVideoDialogOpen(true);
    };


    const handlePageChange = (page: number) => setCurrentPage(page);

    const formatDuration = (duration: string) => {
        const [h, m, s] = duration.split(":").map(Number);
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    const getDurationColor = (duration: string) => {
        const [h, m, s] = duration.split(":").map(Number);
        const totalSeconds = h * 3600 + m * 60 + s;
        if (totalSeconds > 300) return "text-red-600 dark:text-red-400";
        if (totalSeconds > 60) return "text-yellow-600 dark:text-yellow-400";
        return "text-green-600 dark:text-green-400";
    };

    const renderPagination = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        if (endPage - startPage < maxVisiblePages - 1) startPage = Math.max(1, endPage - maxVisiblePages + 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <Button
                    key={i}
                    variant={i === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(i)}
                    className={i === currentPage ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                    {i}
                </Button>
            );
        }
        return pages;
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Breadcrumb */}
            <div className="border-b bg-slate-50 dark:bg-slate-900/50 px-8 py-3">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.history.back()}
                        className="p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span>Tenants</span>
                    <ChevronRight className="w-4 h-4" />
                    <span>{tenantName}</span>
                    <ChevronRight className="w-4 h-4" />
                    <span>{selectedWebUrl}</span>

                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 px-8 py-6">
                <Tabs defaultValue="recordings" className="h-full flex flex-col">
                    <TabsList className="mb-4">
                        <TabsTrigger value="recordings" className="gap-2">
                            <Video className="w-4 h-4" /> Recordings
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="recordings" className="flex-1 flex flex-col mt-0">
                        {/* Filter Controls */}
                        <div className="mb-4 flex items-center gap-4 flex-wrap">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className={cn("w-36 justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {startDate ? format(startDate, "MMM dd, yyyy") : "Start date"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                                </PopoverContent>
                            </Popover>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className={cn("w-36 justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {endDate ? format(endDate, "MMM dd, yyyy") : "End date"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus disabled={(date) => {
                                        if (!startDate) return true;
                                        return date < startDate
                                    }} />
                                </PopoverContent>
                            </Popover>

                            <div className="flex items-center gap-2 flex-1 max-w-md">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <Input
                                        placeholder="Search sessions..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sessions Table */}
                        <div className="flex-1">
                            <Card className="performance-card">
                                <CardContent className="p-0">
                                    <Table className="performance-table">
                                        <TableHeader>
                                            <TableRow className="bg-slate-800 hover:bg-slate-800">
                                                <TableHead className="w-12 text-white font-medium"></TableHead>
                                                <TableHead className="w-80 text-white font-medium">
                                                    <div className="flex items-center gap-2"><User className="w-4 h-4" />Email / Credential</div>
                                                </TableHead>
                                                <TableHead className="w-36 text-white font-medium">Access IP</TableHead>
                                                <TableHead className="w-40 text-white font-medium">Start Date</TableHead>
                                                <TableHead className="w-40 text-white font-medium">End Date</TableHead>
                                                <TableHead className="w-24 text-white font-medium">
                                                    <div className="flex items-center gap-2"><Clock className="w-4 h-4" />Duration</div>
                                                </TableHead>
                                                <TableHead className="w-20 text-center text-white font-medium">Videos</TableHead>
                                            </TableRow>
                                        </TableHeader>

                                        <TableBody>
                                            {loading ? (
                                                Array.from({ length: itemsPerPage }).map((_, i) => (
                                                    <TableRow key={i}>
                                                        {Array.from({ length: 7 }).map((__, j) => (
                                                            <TableCell key={j}>
                                                                <Skeleton className="h-4 w-full" />
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))
                                            ) : currentSessions.length > 0 ? (
                                                currentSessions.map((session, index) => (
                                                    <React.Fragment key={index}>
                                                        <TableRow
                                                            onClick={() => toggleSession(index.toString())}
                                                            className="hover:bg-muted/50 cursor-pointer transition-colors"
                                                        >
                                                            <TableCell>
                                                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                                                    {expandedSessions[index.toString()] ? (
                                                                        <ChevronDown className="w-4 h-4" />
                                                                    ) : (
                                                                        <ChevronRight className="w-4 h-4" />
                                                                    )}
                                                                </Button>
                                                            </TableCell>

                                                            <TableCell>
                                                                <div className="space-y-1">
                                                                    <div className="font-medium text-sm">{session.userEmail}</div>
                                                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                                        <Key className="w-3 h-3" /> {session.credentialEmail}
                                                                    </div>
                                                                </div>
                                                            </TableCell>

                                                            <TableCell>
                                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300">
                                                                    {session.publicIp}
                                                                </span>
                                                            </TableCell>

                                                            <TableCell className="font-mono text-sm">{session.startDate}</TableCell>
                                                            <TableCell className="font-mono text-sm">{session.endDate}</TableCell>

                                                            <TableCell>
                                                                <span className={`font-medium text-sm ${getDurationColor(session.duration)}`}>
                                                                    {formatDuration(session.duration)}
                                                                </span>
                                                            </TableCell>

                                                            <TableCell className="text-center">
                                                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                                                    {session.fileCount}
                                                                </span>
                                                            </TableCell>
                                                        </TableRow>

                                                        {expandedSessions[index.toString()] && (
                                                            <TableRow className="bg-muted/30">
                                                                <TableCell colSpan={7} className="p-0">
                                                                    <div className="px-12 py-4">
                                                                        <div className="text-sm font-semibold mb-3 text-muted-foreground">
                                                                            Files (Zip: {session.zipName})
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            {session.files.map((file, i) => (
                                                                                <div
                                                                                    key={i}
                                                                                    className="flex items-center justify-between gap-4 p-3 bg-background rounded-lg border cursor-pointer hover:underline transition-all"
                                                                                >
                                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center shrink-0">
                                                                                            <Video className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                                                        </div>
                                                                                        <div className="min-w-0 flex-1">
                                                                                            <div className="font-medium text-sm truncate">
                                                                                                {i + 1}. {file}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleViewVideo(session.zipName, file);
                                                                                        }}
                                                                                        className="gap-1.5 shrink-0"
                                                                                    >
                                                                                        <Play className="w-3 h-3" /> View
                                                                                    </Button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                                                        No recording found
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>

                                </CardContent>
                            </Card>
                        </div>

                        {/* Pagination */}
                        {!loading && totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <div className="text-sm text-muted-foreground">
                                    Showing {startIndex + 1} to {Math.min(endIndex, filteredSessions.length)} of {filteredSessions.length} sessions
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</Button>
                                    {renderPagination()}
                                    <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</Button>
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Video Dialog */}
            <Dialog open={isVideoDialogOpen} onOpenChange={setIsVideoDialogOpen}>
                <DialogContent className="sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <Video className="w-5 h-5 text-blue-600" />
                            Video Playback
                        </DialogTitle>
                    </DialogHeader>

                    {selectedVideo ? (
                        <div className="relative w-full" style={{ height: 480 }}>
                            {videoLoading && (
                                <Skeleton className="absolute top-0 left-0 w-full h-full rounded-lg" />
                            )}
                            <video
                                src={`${NGROK}/api/websites/stream-video?zipFile=${encodeURIComponent(selectedZip)}&videoFile=${encodeURIComponent(selectedFile)}`}
                                controls
                                autoPlay
                                style={{ width: "100%", height: "100%", borderRadius: "8px" }}
                                onLoadedData={() => setVideoLoading(false)}
                                onError={() => {
                                    console.error("Video failed to load");
                                    setVideoLoading(false);
                                }}
                            />

                        </div>
                    ) : (
                        <div className="text-center text-muted-foreground py-10">
                            No video selected
                        </div>
                    )}

                </DialogContent>
            </Dialog>

        </div>
    );
}
